/**
 * Equality helpers for `ui.select(selector, equality)`.
 *
 * Default equality on `select()` is `Object.is`. For object slices,
 * consumers should pass {@link shallowEqual} or a custom equality —
 * otherwise every state recompute will produce a new object and re-fire
 * the listener. Same posture as TipTap's `useEditorState` and Slate's
 * `useSlateSelector`.
 */

/** Shallow structural equality for plain objects and arrays. */
export function shallowEqual<T>(a: T, b: T): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) return false;

  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (!Object.is(a[i], b[i])) return false;
    }
    return true;
  }

  if (Array.isArray(b)) return false;

  const keysA = Object.keys(a as Record<string, unknown>);
  const keysB = Object.keys(b as Record<string, unknown>);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (!Object.is((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])) return false;
  }
  return true;
}
